import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ChevronLeft, Dumbbell, Zap, ArrowRight, Lock } from "lucide-react";
import VrijeTrainingSection from "./VrijeTrainingSection";
import DashboardSection from "./DashboardSection";
import BottomNav from "../../components/ui/BottomNav";

const API = process.env.REACT_APP_BACKEND_URL;

export default function TrainingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [schemas, setSchemas] = useState([]);
  const [customSchemas, setCustomSchemas] = useState([]);
  const [bedrijfSchemas, setBedrijfSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const isBedrijfsSporter = user?.role === "sporter";

  useEffect(() => {
    if (!user?.token) return;
    const h = { Authorization: `Bearer ${user.token}` };
    Promise.all([
      fetch(`${API}/api/my-schemas`, { headers: h }),
      fetch(`${API}/api/my-custom-schemas`, { headers: h }),
      fetch(`${API}/api/sporter/schemas`, { headers: h }),
    ]).then(async ([r1, r2, r3]) => {
      if (r1.ok) setSchemas((await r1.json()).schemas || []);
      if (r2.ok) setCustomSchemas((await r2.json()).schemas || []);
      if (r3.ok) setBedrijfSchemas((await r3.json()).schemas || []);
    }).finally(() => setLoading(false));
  }, [user?.token]);

  const alleSchemas = isBedrijfsSporter
    ? bedrijfSchemas
    : [
        ...customSchemas.map(s => ({ ...s, _type: "custom" })),
        ...schemas.map(s => ({ ...s, _type: "schema" })),
      ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="max-w-2xl mx-auto px-4 pt-5 pb-28">
        {/* Back */}
        <button onClick={() => navigate("/account")}
          className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm font-semibold mb-6 transition-colors">
          <ChevronLeft size={16} /> Profiel
        </button>

        <h1 className="text-white font-black text-2xl mb-6">Training</h1>

        {/* B2C Dashboard stats */}
        {!isBedrijfsSporter && <DashboardSection user={user} />}

        {/* Schema's */}
        <div className="account-card mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Dumbbell size={15} className="text-[#C9A84C]" />
            <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">Mijn schema's</p>
          </div>
          {loading ? (
            <div className="space-y-3">
              {[1, 2].map(i => <div key={i} className="h-16 bg-[#1a1a1a] rounded-xl animate-pulse" />)}
            </div>
          ) : alleSchemas.length === 0 ? (
            <div className="text-center py-6">
              <Lock size={22} className="text-zinc-700 mx-auto mb-2" />
              <p className="text-zinc-500 text-sm">Nog geen schema's</p>
              {!isBedrijfsSporter && (
                <button onClick={() => navigate("/")}
                  className="text-[#C9A84C] text-xs font-semibold mt-2 hover:underline">
                  Bekijk schema's →
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {alleSchemas.map((s, i) => (
                <div key={s.id || s.product || i}
                  className="bg-[#1a1a1a] border border-white/5 rounded-xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#C9A84C]/10 flex items-center justify-center shrink-0">
                    <Dumbbell size={17} className="text-[#C9A84C]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{s.naam || s.label}</p>
                    <p className="text-zinc-600 text-xs">
                      {s.trainingsdagen ? `${s.trainingsdagen}x per week` : s.frequency || `${s.dagen?.length || 0} dagen`}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      if (s._type === "custom") navigate(`/custom-schema/${s.id}`);
                      else if (s.product?.startsWith("hyrox-")) navigate(`/hyrox/${s.product}`);
                      else if (s.id) navigate(`/custom-schema/${s.id}`);
                      else navigate(`/schema/${s.product}`);
                    }}
                    data-testid={`schema-btn-${i}`}
                    className="flex items-center gap-1 text-zinc-400 text-xs font-semibold hover:text-white transition-colors shrink-0">
                    <ArrowRight size={13} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Vrije Training */}
        <VrijeTrainingSection user={user} />
      </div>
      <BottomNav />
    </div>
  );
}
